<template>
  <div ref="root" class="st-dropdown">
    <div class="st-dropdown__trigger" @click="toggle">
      <slot name="trigger" :open="isOpen" />
    </div>
    <transition name="st-dropdown">
      <div
        v-if="isOpen"
        class="st-dropdown__menu"
        :class="`st-dropdown__menu--${align}`"
        role="menu"
        @click="onMenuClick"
      >
        <slot :close="close" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';

withDefaults(defineProps<{ align?: 'left' | 'right' }>(), { align: 'left' });

const isOpen = ref(false);
const root = ref<HTMLElement | null>(null);

function toggle() {
  isOpen.value = !isOpen.value;
}
function close() {
  isOpen.value = false;
}
/** Close after a menu item is clicked (items bubble up to the menu). */
function onMenuClick(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('[data-keep-open]')) return;
  close();
}
function onDocClick(e: MouseEvent) {
  if (isOpen.value && root.value && !root.value.contains(e.target as Node)) close();
}
function onEsc(e: KeyboardEvent) {
  if (e.key === 'Escape') close();
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onEsc);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onEsc);
});
</script>

<style scoped>
.st-dropdown {
  position: relative;
  display: inline-flex;
}
.st-dropdown__menu {
  position: absolute;
  top: calc(100% + var(--st-space-1));
  z-index: var(--st-z-dropdown);
  min-width: 11rem;
  padding: var(--st-space-1);
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-md);
  box-shadow: var(--st-shadow-popover);
}
.st-dropdown__menu--left {
  left: 0;
}
.st-dropdown__menu--right {
  right: 0;
}

.st-dropdown-enter-active,
.st-dropdown-leave-active {
  transition:
    opacity var(--st-transition-fast) var(--st-ease),
    transform var(--st-transition-fast) var(--st-ease);
}
.st-dropdown-enter-from,
.st-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
